import React, { useState, useMemo, useContext, Dispatch, SetStateAction, useCallback } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { Link } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import TableTabs from '../TableTabs'
import { useCommonStyles, useDetailStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { useGetDataByPayload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { AdminPlayerTransaction, AdminPlayerCashLogStatistic, AdminPlayerBetHistoryStatistic, NowTime, GameType, AdminCashLogType, PermissionType } from '@golden/gdk-admin'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import { formatDateTime, formatMoney, formatCount } from '../../../utils/default/TableHelper'
import allRoute from '../route/route'
import PointsCell from '../../default/present/PointsCell'
import { InfoContext } from '../../../views/admin/player/PlayerDetailPage'
import { getGameName } from '../../../utils/default/PlatformHelper'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'
import PayoutTip from '../PayoutTip'
import Tip from '../Tip'
import { endOfDay, endOfMonth, getTime, startOfDay, startOfMonth, subDays } from 'date-fns'
import { mergeArray } from '@golden/utils'
import { GAME_ORDER } from '@golden/game-china'
import { useChecker } from '../../../utils/admin/AdminRouteHook'

interface PropTypes {
  id: number
  section: string
  loaded: Dispatch<SetStateAction<string>>
}

interface BetHistoryRowType {
  id: string
  date: React.ReactElement | string
  agents: string
  game: string
  betCount: number | React.ReactElement
  revenue: string
  cash: React.ReactElement | string
  feedback: string
  subRows: BetHistorySubRowType[]
}
interface BetHistorySubRowType {
  id: string
  gameId: GameType
  date: React.ReactElement | string
  agents: string
  game: string
  betCount: React.ReactElement
  revenue: string
  cash: React.ReactElement | string
  feedback: string
  mergedRows: BetHistorSubMergedRowType[]
}

interface BetHistorSubMergedRowType {
  id: string
  game: string
  gameId: GameType
  betCount: React.ReactElement
  revenue: string
  cash: React.ReactElement | string
  feedback: string
}

interface CashLogRowType extends CashLogSubRowType {
  subRows: CashLogSubRowType[]
}
interface CashLogSubRowType {
  id: string
  date: React.ReactElement | string
  agents: string
  deposit: string
  withdraw: string
  activity: string
  transferPoint: string
  manualDepositOther: string
  manualWithdrawOther: string
  manualDepositRc: string
  manualWithdrawRc: string
}

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 600,
    color: theme.palette.common.black,
    paddingBottom: theme.spacing(1)
  },
  head: {
    textAlign: 'right'
  },
  w50: {
    width: '50%'
  },
  cell: {
    backgroundColor: theme.palette.common.white,
    textAlign: 'right',
    padding: theme.spacing(2)
  },
  center: {
    textAlign: 'center'
  },
  left: {
    textAlign: 'left'
  }
}))

const PlayerDetailFinanceSummarySection: React.FC<{ overview: AdminPlayerTransaction }> = (props) => {
  const { overview } = props
  const { t } = useT()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const detailClasses = useDetailStyles()
  const headClassName = clsx(detailClasses.greyHead, classes.head, classes.w50)
  const cellClassName = clsx(detailClasses.cell, classes.cell)

  const [info] = useContext(InfoContext)
  const page = 1
  const account = info.account
  const startAt = subDays(startOfDay(NowTime.get()), 30).getTime()
  const endAt = endOfDay(NowTime.get()).getTime()

  const visibleManualDepositButton = useChecker([PermissionType.MANUAL_DEPOSIT_READ])
  const visibleDepositSlipButton = useChecker([PermissionType.GOLDEN_DEPOSIT_MANAGEMENT_READ])
  const visibleThirdPartyDepositSlipButton = useChecker([PermissionType.THIRD_PARTY_DEPOSIT_MANAGEMENT_READ])
  const visibleAgentWalletButton = useChecker([PermissionType.AGENT_WALLET_MANAGEMENT_READ])
  const visibleManualWithdrawButton = useChecker([PermissionType.MANUAL_WITHDRAW_READ])
  const visibleWithdrawFMManageButton = useChecker([PermissionType.WITHDRAW_FM_SLIP_READ])
  const visibleWithdrawRCManageButton = useChecker([PermissionType.WITHDRAW_RC_SLIP_READ])

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12} md={6} lg={4}>
        <Typography className={classes.title}>{t('common.depositData')}</Typography>
        <table className={detailClasses.table}>
          <tbody>
            <tr>
              <td className={headClassName}>{t('common.depositCountSum')}</td>
              <td className={cellClassName}>{formatCount(overview.deposit.count)}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.depositCashSum')}</td>
              <td className={cellClassName}>{overview.deposit.last_at ? formatMoney(overview.deposit.cash_sum) : 0}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.depositMaxCash')}</td>
              <td className={cellClassName}>{overview.deposit.last_at && overview.deposit.max ? formatMoney(overview.deposit.max) : 0}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.lastDepositDate')}</td>
              <td className={cellClassName}>{overview.deposit.last_at ? formatDateTime(overview.deposit.last_at) : '--'}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.lastDepositCash')}</td>
              <td className={cellClassName}>{overview.deposit.last_at && overview.deposit.last_cash ? formatMoney(overview.deposit.last_cash) : 0}</td>
            </tr>
          </tbody>
        </table>
        <Box paddingTop={2}>
          <Grid container justifyContent="flex-end" spacing={2}>
            {visibleManualDepositButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.manualDeposit.encodePath({ param: {}, search: { page, account, start_at: startAt, end_at: endAt } })}
              >
                {t('page.manualDeposit')}
              </Button>
            </Grid>}
            {visibleDepositSlipButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.depositSlip.encodePath({ param: {}, search: { page, user_account: account, created_at_start: startAt, created_at_end: endAt } })}
              >
                {t('page.depositSlip')}
              </Button>
            </Grid>}
            {visibleThirdPartyDepositSlipButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.depositThirdPartySlip.encodePath({ param: {}, search: { page, account, created_at_start: startAt, created_at_end: endAt } })}
              >
                {t('page.thirdPartyDepositSlip')}
              </Button>
            </Grid>}
            {visibleAgentWalletButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.agentWalletHistory.encodePath({ param: {}, search: { page, account, start_at: startAt, end_at: endAt } })}
              >
                {t('page.agentWallet')}
              </Button>
            </Grid>}
          </Grid>
        </Box>
        <Box paddingTop={2}>
          <PayoutTip color="#808080" />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <Typography className={classes.title}>{t('common.withdrawData')}</Typography>
        <table className={detailClasses.table}>
          <tbody>
            <tr>
              <td className={headClassName}>{t('common.withdrawCountSum')}</td>
              <td className={cellClassName}>{formatCount(overview.withdraw.count)}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.withdrawCashSum')}</td>
              <td className={cellClassName}>{overview.withdraw.last_at ? formatMoney(overview.withdraw.cash_sum) : 0}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.withdrawMaxCash')}</td>
              <td className={cellClassName}>{overview.withdraw.last_at && overview.withdraw.max ? formatMoney(overview.withdraw.max) : 0}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.lastWithdrawDate')}</td>
              <td className={cellClassName}>{overview.withdraw.last_at ? formatDateTime(overview.withdraw.last_at) : '--'}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.lastWithdrawCash')}</td>
              <td className={cellClassName}>{overview.withdraw.last_at && overview.withdraw.last_cash ? formatMoney(overview.withdraw.last_cash) : 0}</td>
            </tr>
          </tbody>
        </table>
        <Box paddingTop={2}>
          <Grid container justifyContent="flex-end" spacing={2}>
            {visibleManualWithdrawButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.manualWithdraw.encodePath({ param: {}, search: { page, account, start_at: startAt, end_at: endAt } })}
              >
                {t('page.manualWithdraw')}
              </Button>
            </Grid>}
            {visibleWithdrawFMManageButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.withdrawalFMSlip.encodePath({ param: {}, search: { page, player_account: account, created_at_start: startAt, created_at_end: endAt } })}
              >
                {t('page.withdrawFMManage')}
              </Button>
            </Grid>}
            {visibleWithdrawRCManageButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.withdrawalRCSlip.encodePath({ param: {}, search: { page, player_account: account, created_at_start: startAt, created_at_end: endAt } })}
              >
                {t('page.withdrawRCManage')}
              </Button>
            </Grid>}
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <Typography className={classes.title}>{t('common.betData')}</Typography>
        <table className={detailClasses.table}>
          <tbody>
            <tr>
              <td className={headClassName}>{t('common.allBetCash')}</td>
              <td className={cellClassName}>{formatMoney(overview.bet.bet_cash_sum)}</td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.playerRevenue')}</td>
              <td className={cellClassName}>
                <PointsCell points={overview.bet.user_revenue} />
              </td>
            </tr>
            <tr>
              <td className={headClassName}>{t('common.playerBetLastAt')}</td>
              <td className={cellClassName}>{overview.bet.last_at ? formatDateTime(overview.bet.last_at) : '--'}</td>
            </tr>
          </tbody>
        </table>
        <Tip text={t('common,betHistoyTip')} />
      </Grid>
    </Grid>
  )
}

const PlayerDetailFinanceBetHistroySection: React.FC<{ betHistory: AdminPlayerBetHistoryStatistic[] }> = (props) => {
  const games = useGDKStore.platform.games()
  const categories = useGDKStore.platform.categories()
  const [info] = useContext(InfoContext)
  const { betHistory } = props

  const { t } = useT()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const detailClasses = useDetailStyles()
  const headClassName = clsx(detailClasses.greyHead, classes.head)
  const cellClassName = clsx(detailClasses.cell, classes.cell)
  const [openedRows, setOpenedRows] = useState<string[]>([])

  const gameOrderMap = new Map<GameType, number>(
    GAME_ORDER.map((type, idx) => [type, idx])
  )
  const betHistoryRows: BetHistoryRowType[] = useMemo(() => {
    return betHistory.reduce<BetHistoryRowType[]>((rows, year, yearIndex) => {
      const id = `${year.year}-${yearIndex}`
      const row = {
        id,
        date: (<span className={year.month_data.length ? commonClasses.anchor : ''} onClick={() => { toggleOpenedRows(id) }}>{year.year || t('common.noDataShort')}</span>),
        agents: year.agents.join(' / ') || t('common.noDataShort'),
        game: mergeArray(categories, year.game_categories, (category, item) => category.instance.id === item).map(([category]) => category.instance.name).join('\n') || t('common.noDataShort'),
        betCount: year.bet_count,
        revenue: year.revenue,
        cash: year.cash,
        feedback: year.feedback,
        subRows: Object.values(year.month_data.reduce<Record<string, BetHistorySubRowType>>((monthGrouped, month, monthIndex) => {
          const date = month.year_month.split('-')
          const searchDate = new Date(Number(date[0]), Number(date[1]) - 1, 1)
          const startAt = getTime(startOfMonth(startOfDay(searchDate)))
          const endAt = getTime(endOfMonth(endOfDay(searchDate)))
          const monthItem = {
            gameId: month.game_id,
            id: `${month.year_month}-${monthIndex}`,
            game: getGameName(month.game_id, games),
            betCount: <Link target="_blank" to={allRoute.playerReportBet.encodePath({ search: { start_at: startAt, end_at: endAt, account: info.account, game_id: month.game_id, page: 1 }, param: {} })}>
                {month.bet_count}
              </Link>,
            revenue: month.revenue,
            cash: month.cash,
            feedback: month.feedback
          }

          if (monthGrouped[month.year_month] !== undefined) {
            const groupOrder = GAME_ORDER.findIndex(item => item === monthGrouped[month.year_month].gameId)
            const order = GAME_ORDER.findIndex(item => item === monthItem.gameId)
            if (order < groupOrder) {
              const TEMP = {
                ...monthGrouped[month.year_month]
              }
              monthGrouped[month.year_month] = {
                ...monthGrouped[month.year_month],
                ...monthItem
              }
              monthGrouped[month.year_month].mergedRows.push(TEMP)
            } else {
              monthGrouped[month.year_month].mergedRows.push(monthItem)
            }
          } else {
            monthGrouped[month.year_month] = {
              ...monthItem,
              date: (<Link target="_blank" to={allRoute.playerCashAccounting.encodePath({ search: { start_at: startAt, end_at: endAt, account: info.account, page: 1 }, param: {} })}>{`${month.year_month.split('-')[1]} ${t('common.month')}`}</Link>),
              agents: month.agents.join(' / '),
              mergedRows: []
            }
          }

          return monthGrouped
        }, {}))
      }
      rows.push(row)
      return rows
    }, [])
  }, [betHistory, t])

  const toggleOpenedRows = useCallback((id: string) => {
    setOpenedRows(value => {
      if (value.includes(id)) {
        return value.filter(item => item !== id)
      }
      return [...value, id]
    })
  }, [setOpenedRows])
  const toggleAllOpenedRows = useCallback(() => {
    setOpenedRows(value => {
      if (value.length === betHistoryRows.length) {
        return []
      }
      return betHistoryRows.map(item => item.id)
    })
  }, [setOpenedRows, betHistoryRows])

  return (
    <Grid container direction="row">
      <Box display='flex' paddingY={1}>
        <Typography className={classes.title}>{t('common.betData')}</Typography>
        <Box className={commonClasses.anchor} marginLeft={1} onClick={toggleAllOpenedRows}>{t('common.toggle')}</Box>
      </Box>
      <table className={detailClasses.table}>
        <thead>
          <tr>
            <th className={clsx(headClassName, classes.center)}>{t('common.date')}</th>
            <th className={clsx(headClassName, classes.center)}>{t('common.agentAccount3')}</th>
            <th className={clsx(headClassName, classes.center)}>{t('common.gamePlatform')}</th>
            <th className={headClassName}>{t('common.betCount2')}</th>
            <th className={headClassName}>{t('common.revenue')}</th>
            <th className={headClassName}>{t('common.allBetCash')}</th>
            <th className={headClassName}>{t('common.allFeedback')}</th>
          </tr>
        </thead>
        <tbody>
          {
            betHistoryRows.map(row => (
              <>
                <tr key={row.id}>
                  <td className={clsx(cellClassName, classes.center)}>{row.date}</td>
                  <td className={clsx(cellClassName, classes.center)}>{row.agents}</td>
                  <td className={clsx(cellClassName, classes.center)}>{row.game}</td>
                  <td className={cellClassName}>{row.betCount}</td>
                  <td className={cellClassName}>
                    {Number(row.revenue) === 0 ? row.revenue : <PointsCell points={row.revenue} />}
                  </td>
                  <td className={cellClassName}>{row.cash}</td>
                  <td className={cellClassName}>{row.feedback}</td>
                </tr>
                {
                  openedRows.includes(row.id) && row.subRows.map(subRow => (
                    <>
                      <tr key={subRow.id}>
                        <td className={clsx(cellClassName)} rowSpan={subRow.mergedRows.length + 1}>{subRow.date}</td>
                        <td className={clsx(cellClassName, classes.center)} rowSpan={subRow.mergedRows.length + 1}>{subRow.agents}</td>
                        <td className={clsx(cellClassName, classes.center)}>{subRow.game}</td>
                        <td className={cellClassName}>{subRow.betCount}</td>
                        <td className={cellClassName}><PointsCell points={subRow.revenue} /></td>
                        <td className={cellClassName}>
                          {subRow.cash}
                        </td>
                        <td className={cellClassName}>{subRow.feedback}</td>
                      </tr>
                      {
                        subRow.mergedRows.sort((a, b) => {
                          const idxA = gameOrderMap.get(a.gameId) ?? Infinity
                          const idxB = gameOrderMap.get(b.gameId) ?? Infinity
                          return idxA - idxB
                        }).map(mergeRow => (
                          <tr key={mergeRow.id}>
                            <td className={clsx(cellClassName, classes.center)}>{mergeRow.game}</td>
                            <td className={cellClassName}>{mergeRow.betCount}</td>
                            <td className={cellClassName}><PointsCell points={mergeRow.revenue} /></td>
                            <td className={cellClassName}>
                              {mergeRow.cash}
                            </td>
                            <td className={cellClassName}>{mergeRow.feedback}</td>
                          </tr >
                        ))
                      }
                    </>
                  ))
                }
              </>
            ))
          }
        </tbody>
      </table>
      <Box whiteSpace="pre" paddingY={1}>
        <Typography style={{ color: '#808080' }}>
          {t('common.playerFinanceStatisticTip')}
        </Typography>
      </Box>
    </Grid>
  )
}

const PlayerDetailFinanceCashLogSection: React.FC<{ cashLog: AdminPlayerCashLogStatistic[] }> = (props) => {
  const [info] = useContext(InfoContext)
  const { cashLog } = props

  const { t } = useT()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const detailClasses = useDetailStyles()
  const headClassName = clsx(detailClasses.greyHead, classes.head)
  const cellClassName = clsx(detailClasses.cell, classes.cell)
  const [openedRows, setOpenedRows] = useState<string[]>([])

  const cashLogRows: CashLogRowType[] = useMemo(() => {
    return cashLog.reduce<CashLogRowType[]>((rows, year, yearIndex) => {
      const id = `${year.year}-${yearIndex}`
      const row = {
        id,
        date: (<span className={year.month_data.length ? commonClasses.anchor : ''} onClick={() => { toggleOpenedRows(id) }}>{year.year || t('common.noDataShort')}</span>),
        agents: year.agents.join(' / ') || t('common.noDataShort'),
        deposit: year.deposit,
        withdraw: year.withdraw,
        activity: year.activity,
        transferPoint: year.transfer_point,
        manualDepositOther: year.manual_deposit_other,
        manualWithdrawOther: year.manual_withdraw_other,
        manualDepositRc: year.manual_deposit_rc,
        manualWithdrawRc: year.manual_withdraw_rc,
        subRows: year.month_data.reduce<CashLogSubRowType[]>((monthRows, month, monthIndex) => {
          const date = month.year_month.split('-')
          const searchDate = new Date(Number(date[0]), Number(date[1]) - 1, 1)
          const startAt = getTime(startOfMonth(startOfDay(searchDate)))
          const endAt = getTime(endOfMonth(endOfDay(searchDate)))

          const categories: AdminCashLogType[] = [AdminCashLogType.DEPOSIT, AdminCashLogType.WITHDRAW, AdminCashLogType.ACTIVITY, AdminCashLogType.AGENT_TRANSFER, AdminCashLogType.RC_ADJUSTMENT, AdminCashLogType.OTHER]

          const monthItem = {
            id: `${month.year_month}-${monthIndex}`,
            date: (<Link target="_blank" to={allRoute.playerReportCash.encodePath({ search: { start_at: startAt, end_at: endAt, account: info.account, categories, page: 1 }, param: {} })}>{`${month.year_month.split('-')[1]} ${t('common.month')}`}</Link>),
            deposit: month.deposit,
            withdraw: month.withdraw,
            activity: month.activity,
            agents: month.agents.join(' / '),
            transferPoint: month.transfer_point,
            manualDepositOther: month.manual_deposit_other,
            manualWithdrawOther: month.manual_withdraw_other,
            manualDepositRc: month.manual_deposit_rc,
            manualWithdrawRc: month.manual_withdraw_rc
          }
          monthRows.push(monthItem)

          return monthRows
        }, [])
      }
      rows.push(row)
      return rows
    }, [])
  }, [cashLog, t])

  const toggleOpenedRows = useCallback((id: string) => {
    setOpenedRows(value => {
      if (value.includes(id)) {
        return value.filter(item => item !== id)
      }
      return [...value, id]
    })
  }, [setOpenedRows])
  const toggleAllOpenedRows = useCallback(() => {
    setOpenedRows(value => {
      if (value.length === cashLogRows.length) {
        return []
      }
      return cashLogRows.map(item => item.id)
    })
  }, [setOpenedRows, cashLogRows])

  return (
    <Grid container direction="row">
      <Box display='flex' paddingY={1}>
        <Typography className={classes.title}>{t('common.cashData')}</Typography>
        <Box className={commonClasses.anchor} marginLeft={1} onClick={toggleAllOpenedRows}>{t('common.toggle')}</Box>
      </Box>
      <table className={detailClasses.table}>
        <thead>
          <tr>
            <th className={clsx(headClassName, classes.center)}>{t('common.date')}</th>
            <th className={headClassName}>{t('common.agentAccount3')}</th>
            <th className={headClassName}>{t('common.allDepositCash')}</th>
            <th className={headClassName}>{t('common.allWithdrawalCash')}</th>
            <th className={headClassName}>{t('common.allActivityCash')}</th>
            <th className={headClassName}>{t('common.transferPoint')}</th>
            <th className={headClassName}>{t('common.manualDepositOther')}</th>
            <th className={headClassName}>{t('common.manualWithdrawOther')}</th>
            <th className={headClassName}>{t('common.manualDepositRC')}</th>
            <th className={headClassName}>{t('common.manualWithdrawRC')}</th>

          </tr>
        </thead>
        <tbody>
          {
            cashLogRows.map(row => (
              <>
                <tr key={row.id}>
                  <td className={clsx(cellClassName, classes.center)}>{row.date}</td>
                  <td className={cellClassName}>{row.agents}</td>
                  <td className={cellClassName}>{row.deposit}</td>
                  <td className={cellClassName}>{row.withdraw}</td>
                  <td className={cellClassName}>{row.activity}</td>
                  <td className={cellClassName}>{row.transferPoint}</td>
                  <td className={cellClassName}>{row.manualDepositOther}</td>
                  <td className={cellClassName}>{row.manualWithdrawOther}</td>
                  <td className={cellClassName}>{row.manualDepositRc}</td>
                  <td className={cellClassName}>{row.manualWithdrawRc}</td>
                </tr>
                {
                  openedRows.includes(row.id) && row.subRows.map(subRow => (
                    <tr key={subRow.id}>
                      <td className={cellClassName}>{subRow.date}</td>
                      <td className={cellClassName}>{subRow.agents}</td>
                      <td className={cellClassName}>{subRow.deposit}</td>
                      <td className={cellClassName}>{subRow.withdraw}</td>
                      <td className={cellClassName}>{subRow.activity}</td>
                      <td className={cellClassName}>{subRow.transferPoint}</td>
                      <td className={cellClassName}>{subRow.manualDepositOther}</td>
                      <td className={cellClassName}>{subRow.manualWithdrawOther}</td>
                      <td className={cellClassName}>{subRow.manualDepositRc}</td>
                      <td className={cellClassName}>{subRow.manualWithdrawRc}</td>
                    </tr>
                  ))
                }
              </>
            ))
          }
        </tbody>
      </table>
    </Grid>
  )
}

const PlayerDetailFinanceSection: React.FC<PropTypes> = (props) => {
  const { id, section, loaded } = props
  const [tab, setTab] = useState<'overview' | 'statistic'>('overview')
  const [overview, setSummary] = useState<AdminPlayerTransaction>({
    deposit: {
      count: 0,
      cash_sum: '0.0000',
      max: '0.0000',
      last_at: new Date(),
      last_cash: '0.0000'
    },
    withdraw: {
      count: 0,
      cash_sum: '0.0000',
      max: '0.0000',
      last_at: new Date(),
      last_cash: '0.0000',
      withdraw_point: '0.0000',
      barrier_sum: '0.0000'
    },
    bet: {
      bet_cash_sum: '0.0000',
      user_revenue: '0.0000',
      last_at: new Date()
    }
  })
  const [betHistory, setBetHistory] = useState<AdminPlayerBetHistoryStatistic[]>([])
  const [cashLog, setCashLog] = useState<AdminPlayerCashLogStatistic[]>([])

  const gdk = useGDK()
  const pageFlow = usePageFlow()
  const commonClasses = useCommonStyles()
  const { t } = useT()

  useGetDataByPayload({
    payload: id,
    canLoadData: tab === 'overview',
    gdkFunc: (payload) => gdk.player.getPlayerTransaction(payload),
    gdkFuncDependencies: [gdk, tab],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (overview: AdminPlayerTransaction) => {
      setSummary(overview)
      pageFlow.setContentShow()
      loaded(section)
    },
    onError: pageFlow.setGDKError
  })

  useGetDataByPayload({
    payload: id,
    canLoadData: tab === 'statistic',
    gdkFunc: (payload) => gdk.player.getPlayerBetHistoryStatistic(payload),
    gdkFuncDependencies: [gdk, tab],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (statistic: { data: AdminPlayerBetHistoryStatistic[] }) => {
      setBetHistory(statistic.data)
      pageFlow.setContentShow()
      loaded(section)
    },
    onError: pageFlow.setGDKError
  })

  useGetDataByPayload({
    payload: id,
    canLoadData: tab === 'statistic',
    gdkFunc: (payload) => gdk.player.getPlayerCashLogStatistic(payload),
    gdkFuncDependencies: [gdk, tab],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (statistic: { data: AdminPlayerCashLogStatistic[] }) => {
      setCashLog(statistic.data)
      pageFlow.setContentShow()
      loaded(section)
    },
    onError: pageFlow.setGDKError
  })

  const tabs = useMemo(() => [
    { label: t('common.playerOverview'), value: 'overview' },
    { label: t('common.activeTime'), value: 'statistic' }
  ], [t])

  return (
    <div id={section}>
      <Paper>
        <Box padding={4}>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={commonClasses.pinkTitleBar}
          >
            <Typography variant="h5">
              {t('common.playerOverview')}
            </Typography>
          </Box>
          <Box display="flex" paddingTop={2}>
            <TableTabs
              value={tab}
              tabs={tabs}
              onChange={(value) => {
                setTab(value as 'overview' | 'statistic')
              }}
            />
          </Box>
          <Box paddingY={2}>
            <LoadingAndErrorFrame { ...pageFlow.status }>
              {
                tab === 'overview'
                  ? <PlayerDetailFinanceSummarySection overview={overview} />
                  : (<>
                  <PlayerDetailFinanceBetHistroySection betHistory={betHistory} />
                  <PlayerDetailFinanceCashLogSection cashLog={cashLog} />
                </>)
              }
            </LoadingAndErrorFrame>
          </Box>
        </Box>
      </Paper>
    </div>
  )
}

export default React.memo(PlayerDetailFinanceSection)
